import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Logo";
import { Plus, Edit2, Trash2, LogOut, Upload, X, Star, Copy, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import { buildImageUrl } from "@/lib/api";
import { CATEGORIES_PHONES, CATEGORIES_ACCESSORIES } from "@/lib/constants";
import FeaturedManager from "./admin/FeaturedManager";
import BulkImportPanel from "./admin/BulkImportPanel";
import BrandsManager from "./admin/BrandsManager";
import CategoriesManager from "./admin/CategoriesManager";
import HomepageEditor from "./admin/HomepageEditor";
import InquiriesTab from "./admin/InquiriesTab";
import MediaLibrary from "./admin/MediaLibrary";

const empty = {
  name: "", brand: "", model: "", category: "iphone", subcategory: "", type: "phone",
  price: 0, currency: "AED", storage_options: [], colors: [],
  condition: "New", availability: "Available", description: "",
  specifications: {}, images: [],
  featured: false, new_arrival: false, hot_deal: false, hot_pick: false, active: true, display_order: 999,
  name_ar: "", description_ar: "",
};

const ALL_CATS = [...CATEGORIES_PHONES.filter((c) => c.id !== "all"), ...CATEGORIES_ACCESSORIES.filter((c) => c.id !== "all")];

export default function AdminDashboardPage() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [storageInput, setStorageInput] = useState("");
  const [colorInput, setColorInput] = useState("");
  const [specKey, setSpecKey] = useState("");
  const [specVal, setSpecVal] = useState("");

  const loadAll = async () => {
    try {
      const [p, i, s] = await Promise.all([
        api.get("/products", { params: { include_inactive: true } }),
        api.get("/admin/inquiries"),
        api.get("/settings"),
      ]);
      setProducts(p.data);
      setInquiries(i.data);
      setSettings(s.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("afc_admin_token");
        nav("/admin/login");
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("afc_admin_token")) {
      nav("/admin/login");
      return;
    }
    document.title = "Admin | AFC TELECOM";
    loadAll();
  }, []);

  const logout = () => {
    localStorage.removeItem("afc_admin_token");
    nav("/admin/login");
  };

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setStorageInput(""); setColorInput(""); setSpecKey(""); setSpecVal("");
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.brand || !form.category) {
      toast.error("Name, brand, category required");
      return;
    }
    try {
      const payload = { ...form, price: Number(form.price) };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
      } else {
        await api.post(`/admin/products`, payload);
      }
      toast.success("Saved.");
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) { toast.error("Delete failed"); }
  };

  const duplicate = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/duplicate`);
      toast.success("Duplicated");
      loadAll();
    } catch { toast.error("Duplicate failed"); }
  };

  const toggleActive = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/toggle-active`);
      toast.success(p.active === false ? "Published" : "Hidden");
      loadAll();
    } catch { toast.error("Update failed"); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-9" />
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Admin</span>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full" data-testid="admin-logout">
            <LogOut className="w-4 h-4 me-2" /> Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl font-bold text-[#0B132B]">Admin Dashboard</h1>

        <Tabs defaultValue="products" className="mt-6">
          <TabsList data-testid="admin-tabs" className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="featured" data-testid="tab-featured">Featured</TabsTrigger>
            <TabsTrigger value="brands" data-testid="tab-brands">Brands</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="homepage" data-testid="tab-homepage">Homepage</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-6">
            <BulkImportPanel onImported={loadAll} />
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-500">{products.length} products</p>
              <Button onClick={openNew} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid="admin-add-product">
                <Plus className="w-4 h-4 me-2" /> Add Product
              </Button>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-start p-4">Product</th>
                    <th className="text-start p-4 hidden md:table-cell">Category</th>
                    <th className="text-start p-4 hidden md:table-cell">Price</th>
                    <th className="text-start p-4 hidden lg:table-cell">Availability</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100" data-testid={`admin-row-${p.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col gap-1 items-start">
                            {p.active === false && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200" data-testid={`row-hidden-${p.id}`}>
                                Hidden
                              </span>
                            )}
                            {(p.brand || "").toLowerCase() === "blackview" && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#1E5FD9]/10 text-[#1E5FD9] border border-[#1E5FD9]/30 font-semibold" data-testid={`row-blackview-${p.id}`}>
                                Blackview
                              </span>
                            )}
                            {p.featured && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`row-featured-${p.id}`}>
                                <Star className="w-3 h-3" /> Featured
                              </span>
                            )}
                            {p.new_arrival && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`row-newarrival-${p.id}`}>
                                New
                              </span>
                            )}
                            {p.hot_deal && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid={`row-hotdeal-${p.id}`}>
                                Hot Deal
                              </span>
                            )}
                            {p.hot_pick && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#1E5FD9]/10 text-[#1E5FD9] border border-[#1E5FD9]/40 font-semibold" data-testid={`row-hotpick-${p.id}`}>
                                ★ Hot Pick
                              </span>
                            )}
                          </div>
                          {p.images?.[0] ? (
                            <img src={buildImageUrl(p.images[0])} alt="" className="w-12 h-12 object-contain bg-slate-50 rounded-lg" />
                          ) : (
                            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-300 text-[10px] uppercase tracking-wider">
                              No img
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{p.name}</p>
                            <p className="text-xs text-slate-500">{p.brand}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell text-slate-600">{p.category}</td>
                      <td className="p-4 hidden md:table-cell">{p.price} {p.currency}</td>
                      <td className="p-4 hidden lg:table-cell">{p.availability}</td>
                      <td className="p-4">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => toggleActive(p)} className="rounded-full" title={p.active === false ? "Show" : "Hide"} data-testid={`toggle-active-${p.id}`}>
                            {p.active === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => duplicate(p)} className="rounded-full" title="Duplicate" data-testid={`duplicate-${p.id}`}>
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="rounded-full" title="Edit" data-testid={`edit-${p.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => del(p)} className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50" title="Delete" data-testid={`delete-${p.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="featured" className="mt-6">
            <FeaturedManager products={products} onChanged={loadAll} />
          </TabsContent>

          <TabsContent value="brands" className="mt-6">
            <BrandsManager />
          </TabsContent>

          <TabsContent value="categories" className="mt-6">
            <CategoriesManager />
          </TabsContent>

          <TabsContent value="homepage" className="mt-6">
            <HomepageEditor />
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            <InquiriesTab />
          </TabsContent>

          <TabsContent value="media" className="mt-6">
            <MediaLibrary />
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            {settings && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 max-w-3xl">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Website Name</Label><Input value={settings.website_name || ""} onChange={(e) => setSettings({ ...settings, website_name: e.target.value })} className="mt-1" data-testid="s-website-name" /></div>
                  <div>
                    <Label>Default Currency</Label>
                    <Select value={settings.default_currency} onValueChange={(v) => setSettings({ ...settings, default_currency: v })}>
                      <SelectTrigger className="mt-1" data-testid="s-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AED">AED</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="IQD">IQD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Logo URL</Label><Input value={settings.logo_url || ""} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} className="mt-1" data-testid="s-logo-url" /></div>
                  <div><Label>Favicon URL</Label><Input value={settings.favicon_url || ""} onChange={(e) => setSettings({ ...settings, favicon_url: e.target.value })} className="mt-1" data-testid="s-favicon-url" /></div>
                  <div><Label>SEO Title</Label><Input value={settings.seo_title || ""} onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })} className="mt-1" data-testid="s-seo-title" /></div>
                  <div><Label>SEO Description</Label><Input value={settings.seo_description || ""} onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })} className="mt-1" data-testid="s-seo-desc" /></div>
                  <div><Label>Sales Name</Label><Input value={settings.sales_name || ""} onChange={(e) => setSettings({ ...settings, sales_name: e.target.value })} className="mt-1" data-testid="s-sales-name" /></div>
                  <div><Label>Sales Phone</Label><Input value={settings.sales_phone || ""} onChange={(e) => setSettings({ ...settings, sales_phone: e.target.value })} className="mt-1" /></div>
                  <div><Label>WhatsApp Sales (no +)</Label><Input value={settings.whatsapp_sales || ""} onChange={(e) => setSettings({ ...settings, whatsapp_sales: e.target.value })} className="mt-1" /></div>
                  <div><Label>Management Name</Label><Input value={settings.management_name || ""} onChange={(e) => setSettings({ ...settings, management_name: e.target.value })} className="mt-1" /></div>
                  <div><Label>Management Phone</Label><Input value={settings.management_phone || ""} onChange={(e) => setSettings({ ...settings, management_phone: e.target.value })} className="mt-1" /></div>
                  <div><Label>WhatsApp Management</Label><Input value={settings.whatsapp_management || ""} onChange={(e) => setSettings({ ...settings, whatsapp_management: e.target.value })} className="mt-1" /></div>
                  <div className="sm:col-span-2"><Label>Location / Address</Label><Input value={settings.location || ""} onChange={(e) => setSettings({ ...settings, location: e.target.value })} className="mt-1" /></div>
                  <div><Label>Email — Sales</Label><Input type="email" value={settings.email_sales || ""} onChange={(e) => setSettings({ ...settings, email_sales: e.target.value })} className="mt-1" data-testid="s-email-sales" /></div>
                  <div><Label>Email — Info</Label><Input type="email" value={settings.email_info || ""} onChange={(e) => setSettings({ ...settings, email_info: e.target.value })} className="mt-1" data-testid="s-email-info" /></div>
                  <div><Label>USD Rate (1 AED = ?)</Label><Input type="number" step="0.001" value={settings.rate_usd ?? ""} onChange={(e) => setSettings({ ...settings, rate_usd: parseFloat(e.target.value) })} className="mt-1" data-testid="s-rate-usd" /></div>
                  <div><Label>IQD Rate (1 AED = ?)</Label><Input type="number" step="0.1" value={settings.rate_iqd ?? ""} onChange={(e) => setSettings({ ...settings, rate_iqd: parseFloat(e.target.value) })} className="mt-1" data-testid="s-rate-iqd" /></div>
                  <div><Label>Facebook URL</Label><Input value={settings.social_facebook || ""} onChange={(e) => setSettings({ ...settings, social_facebook: e.target.value })} className="mt-1" data-testid="s-social-facebook" /></div>
                  <div><Label>Instagram URL</Label><Input value={settings.social_instagram || ""} onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })} className="mt-1" data-testid="s-social-instagram" /></div>
                  <div><Label>Twitter/X URL</Label><Input value={settings.social_twitter || ""} onChange={(e) => setSettings({ ...settings, social_twitter: e.target.value })} className="mt-1" data-testid="s-social-twitter" /></div>
                  <div><Label>LinkedIn URL</Label><Input value={settings.social_linkedin || ""} onChange={(e) => setSettings({ ...settings, social_linkedin: e.target.value })} className="mt-1" data-testid="s-social-linkedin" /></div>
                </div>
                <Button onClick={saveSettings} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid="save-settings">Save Settings</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="f-name" /></div>
              <div><Label>Brand *</Label>
                <Input list="brand-options" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1" data-testid="f-brand" placeholder="e.g. Blackview" />
                <datalist id="brand-options">
                  <option value="Blackview" />
                  <option value="Apple" />
                  <option value="Samsung" />
                  <option value="Xiaomi" />
                  <option value="TECNO" />
                  <option value="Infinix" />
                  <option value="Huawei" />
                  <option value="HONOR" />
                  <option value="OPPO" />
                  <option value="vivo" />
                  <option value="OnePlus" />
                  <option value="Google Pixel" />
                  <option value="Motorola" />
                  <option value="Nokia" />
                  <option value="Sony" />
                  <option value="ASUS" />
                  <option value="Nothing" />
                  <option value="realme" />
                  <option value="ZTE" />
                  <option value="TCL" />
                </datalist>
              </div>
              <div><Label>Model</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="accessory">Accessory</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="f-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ALL_CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c.en}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Price (AED) *</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" data-testid="f-price" /></div>
              <div><Label>Condition</Label><Input value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Availability</Label>
                <Select value={form.availability} onValueChange={(v) => setForm({ ...form, availability: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Out of Stock">Out of Stock</SelectItem>
                    <SelectItem value="On Request">On Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Name (Arabic)</Label>
                <Input dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} className="mt-1" data-testid="f-name-ar" />
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <label className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2.5 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="f-featured" />
                  <span className="text-sm">Featured</span>
                </label>
                <label className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2.5 cursor-pointer">
                  <input type="checkbox" checked={form.new_arrival || false} onChange={(e) => setForm({ ...form, new_arrival: e.target.checked })} data-testid="f-new-arrival" />
                  <span className="text-sm">New Arrival</span>
                </label>
                <label className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2.5 cursor-pointer">
                  <input type="checkbox" checked={form.hot_deal || false} onChange={(e) => setForm({ ...form, hot_deal: e.target.checked })} data-testid="f-hot-deal" />
                  <span className="text-sm">Hot Deal</span>
                </label>
                <label className="inline-flex items-center gap-2 border border-[#1E5FD9]/40 rounded-full px-4 py-2.5 cursor-pointer bg-[#1E5FD9]/5">
                  <input type="checkbox" checked={form.hot_pick || false} onChange={(e) => setForm({ ...form, hot_pick: e.target.checked })} data-testid="f-hot-pick" />
                  <span className="text-sm font-semibold text-[#1E5FD9]">★ Hot Pick</span>
                </label>
              </div>
              <div className="sm:col-span-2">
                <Label>Description (Arabic)</Label>
                <Textarea dir="rtl" rows={2} value={form.description_ar} onChange={(e) => setForm({ ...form, description_ar: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div>
              <Label>Storage Options</Label>
              <div className="mt-1 flex gap-2">
                <Input value={storageInput} onChange={(e) => setStorageInput(e.target.value)} placeholder="e.g. 256GB" />
                <Button type="button" variant="outline" onClick={() => { if (storageInput) { setForm({ ...form, storage_options: [...form.storage_options, storageInput] }); setStorageInput(""); } }}>Add</Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {form.storage_options.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-xs">
                    {s} <button onClick={() => setForm({ ...form, storage_options: form.storage_options.filter((_, x) => x !== i) })}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <Label>Colors</Label>
              <div className="mt-1 flex gap-2">
                <Input value={colorInput} onChange={(e) => setColorInput(e.target.value)} placeholder="e.g. Black" />
                <Button type="button" variant="outline" onClick={() => { if (colorInput) { setForm({ ...form, colors: [...form.colors, colorInput] }); setColorInput(""); } }}>Add</Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {form.colors.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-xs">
                    {c} <button onClick={() => setForm({ ...form, colors: form.colors.filter((_, x) => x !== i) })}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <Label>Specifications</Label>
              <div className="mt-1 grid grid-cols-[1fr_1fr_auto] gap-2">
                <Input value={specKey} onChange={(e) => setSpecKey(e.target.value)} placeholder="Key" />
                <Input value={specVal} onChange={(e) => setSpecVal(e.target.value)} placeholder="Value" />
                <Button type="button" variant="outline" onClick={() => { if (specKey) { setForm({ ...form, specifications: { ...form.specifications, [specKey]: specVal } }); setSpecKey(""); setSpecVal(""); } }}>Add</Button>
              </div>
              <div className="mt-2 space-y-1">
                {Object.entries(form.specifications).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                    <span><b>{k}:</b> {v}</span>
                    <button onClick={() => { const s = { ...form.specifications }; delete s[k]; setForm({ ...form, specifications: s }); }}><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Images</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {form.images.map((im, i) => (
                  <div key={i} className="relative w-20 h-20 bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
                    <img src={buildImageUrl(im)} alt="" className="w-full h-full object-contain" />
                    <button onClick={() => setForm({ ...form, images: form.images.filter((_, x) => x !== i) })} className="absolute top-1 end-1 bg-white/80 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-[#1E5FD9]">
                  <Upload className="w-5 h-5 text-slate-400" />
                  <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="f-upload" />
                </label>
              </div>
              <p className="mt-1 text-xs text-slate-500">Or paste a URL:</p>
              <div className="flex gap-2 mt-1">
                <Input placeholder="https://…" onKeyDown={(e) => {
                  if (e.key === "Enter" && e.target.value) {
                    setForm({ ...form, images: [...form.images, e.target.value] });
                    e.target.value = "";
                  }
                }} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid="f-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
